import Header from "../../components/Header";
import BoxContainer from "../../components/BoxContainer";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { getResignationDetails } from "../../services/exitManagement";
import useLoader from "../../hooks/useLoader";
import { defaultDateFormatter } from "../../Helper/functionHelper";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/Table";
import { TextField, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import useAuthUser from "../../hooks/useAuthUser";
import TableLayout from "../../components/layout/TableLayout";
import CustomizedButton from "../../components/ui/Button";
import AcceptRejectResignationForm from "../../components/exit-management/ApplyRejectResignationForm";
import useModal from "../../hooks/useModal";
import EditIcon from "@mui/icons-material/Edit";
import EditActualLastWorkingDay from "../../components/exit-management/EditActualLastWorkingDay";
import UploadDocumentForm from "../../components/exit-management/UploadDocumentForm";
import DocumentsTable from "../../components/exit-management/DocumentsTable";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WithdrawResignation from "../../components/exit-management/WithdrawResignationForm";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
const ViewResignation = () => {
  const [resignationData, setResignationData] = useState({});
  const { resignationId } = useParams();
  const { hideLoader, showLoader } = useLoader();
  const [withdrawalSetting, setWithdrawalSetting] = useState({});
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [buyoutDays, setBuyoutDays] = useState("");
  const [payableAmount, setPayableAmount] = useState("");
  const [actualLastWorkingDate, setActualLastWorkingDate] = useState(null);

  const { loggedInUser } = useAuthUser();
  const userRole = loggedInUser?.role;
  const { showModal } = useModal();
  const isAdminOrHr = userRole === "1" || userRole === "2";
  const isEmployee = userRole === "7";

  const fetchResignationDetails = async (id) => {
    try {
      showLoader();
      const response = await getResignationDetails(id);
      if (response.status === 200) {
        const result = response.data.result || {};
        setResignationData(result);
        setWithdrawalSetting(response.data.companyDetails || {});
        setEffectiveDate(result.createdAt ? new Date(result.createdAt) : null);
        setBuyoutDays(result?.buyoutDays || "");
        setActualLastWorkingDate(
          result?.actualLastWorkingDay
            ? new Date(result.actualLastWorkingDay)
            : null
        );
      } else {
        console.error("Failed to fetch resignation details.");
      }
    } catch (error) {
      console.error("Error fetching resignation details:", error);
    } finally {
      hideLoader();
    }
  };

  const canWithdraw = (createdAt, status) => {
    const { resignationWithdrawalAllowed, resignationWithdrawalDays } =
      withdrawalSetting;

    if (!resignationWithdrawalAllowed || status !== 0) return false;

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const createdDate = new Date(createdAt);
    createdDate.setHours(0, 0, 0, 0);

    const daysDiff = Math.floor(
      (currentDate - createdDate) / (1000 * 60 * 60 * 24)
    );

    if (status === 0) return true;
    if (status === 1) return false;
    return (
      daysDiff <= resignationWithdrawalDays &&
      status !== 3 &&
      status !== 6 &&
      status !== 9
    );
  };

  const statusMap = {
    0: "Applied",
    1: "Withdrawn",
    2: "Accepted By Manager",
    3: "Rejected By Manager",
    4: "Round Table Scheduled By Manager",
    5: "Accepted By HR",
    6: "Rejected By HR",
    7: "Updated Last Working Day By HR",
    8: "Document Uploaded By HR",
    9: "Retained By HR",
  };

  const reasonMap = {
    other: "Other",
    relocation: "Relocation",
    personal: "Personal Reason",
    new_opportunity: "New Opportunity",
  };

  let status = statusMap[resignationData.status] || "--";
  if (resignationData.status === 4) {
    const formattedDate = defaultDateFormatter(
      resignationData?.roundTableDateTime
    );
    const isoTime = resignationData?.roundTableDateTime.split("T")[1];
    let time = "";
    if (isoTime) {
      const [hours, minutes] = isoTime.slice(0, 5).split(":");
      const date = new Date();
      date.setUTCHours(hours, minutes);
      time = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
    status += ` (${formattedDate} ${time})`;
  }

  const ResignationField = ({ label, value }) => (
    <div className="col-md-4">
      <span className="fw-bold">{label}</span>
      <p>{value || "--"}</p>
    </div>
  );

  const handleModalOpen = (title, action) => {
    let body = null;
    if (action === "hraccept") {
      if (resignationData?.buyout) {
        if (buyoutDays === "") {
          toast.error("Please enter the approved buyout days.");
          return;
        } else if (buyoutDays < 0) {
          toast.error("Approved buyout days cannot be negative.");
          return;
        } else if (!Number.isInteger(Number(buyoutDays))) {
          toast.error("Approved buyout days must be a valid number.");
          return;
        } else if (buyoutDays > resignationData?.noticePeriodDays) {
          toast.error(
            "Approved buyout days cannot be greater than notice period days."
          );
          return;
        }
        if (
          payableAmount === "" ||
          payableAmount === null ||
          payableAmount === undefined ||
          payableAmount === "0"
        ) {
          toast.error("Please enter the payable amount.");
          return;
        } else if (!Number.isInteger(Number(payableAmount))) {
          toast.error("Payable amount must be a valid number.");
          return;
        } else if (payableAmount < 0) {
          toast.error("Payable amount cannot be negative.");
          return;
        }
      }
      if (effectiveDate === null) {
        toast.error("Please select the effective date.");
        return;
      }
      if (actualLastWorkingDate === null) {
        toast.error("Please select the actual last working date.");
        return;
      }

      body = (
        <AcceptRejectResignationForm
          resignationId={resignationId}
          action="hraccept"
          fetchResignations={() => fetchResignationDetails(resignationId)}
          acceptData={{
            buyoutDays,
            effectiveDate,
            actualLastWorkingDate,
            payableAmount,
          }}
        />
      );
    } else if (action === "hrreject") {
      body = (
        <AcceptRejectResignationForm
          resignationId={resignationId}
          action="hrreject"
          fetchResignations={() => fetchResignationDetails(resignationId)}
        />
      );
    } else if (action === "editLWD") {
      body = (
        <EditActualLastWorkingDay
          resignationId={resignationId}
          fetchResignations={() => fetchResignationDetails(resignationId)}
        />
      );
    } else if (action === "hrUpload") {
      body = (
        <UploadDocumentForm
          resignationId={resignationId}
          fetchResignations={() => fetchResignationDetails(resignationId)}
          action="hrUpload"
        />
      );
    } else if (action === "empUpload") {
      body = (
        <UploadDocumentForm
          resignationId={resignationId}
          fetchResignations={() => fetchResignationDetails(resignationId)}
          action="empUpload"
        />
      );
    } else if (action === "hr-retain") {
      body = (
        <WithdrawResignation
          resignationId={resignationId}
          action={action}
          fetchResignations={() => fetchResignationDetails(resignationId)}
        />
      );
    } else if (action === "employee-withdraw") {
      body = (
        <WithdrawResignation
          resignationId={resignationId}
          action={action}
          fetchResignations={() => fetchResignationDetails(resignationId)}
        />
      );
    }
    showModal(title, body);
  };

  useEffect(() => {
    if (resignationId) {
      fetchResignationDetails(resignationId);
    }
  }, [resignationId]);

  const showRetainButton =
    isAdminOrHr &&
    (resignationData.status === 0 ||
      resignationData.status === 2 ||
      resignationData.status === 4 ||
      resignationData.status === 5);

  const showBuyoutDaysField =
    isAdminOrHr && resignationData.status === 2 && resignationData?.buyout;
  return (
    <>
      <Header
        title={
          <>
            Resignation Details of{" "}
            <span>
              {resignationData?.employeeDetails?.fullName}(
              {resignationData?.employeeDetails?.employeeId})
            </span>
          </>
        }
      />
      <BoxContainer>
        <div className="row">
          <div className="col-md-12">
            <span className="fw-bold">Resignation Email</span>
            <p style={{ whiteSpace: "pre-wrap" }}>{resignationData?.email}</p>
          </div>
        </div>

        <div className="row">
          <ResignationField
            label="Reason for Resignation"
            value={
              resignationData?.reason
                ? resignationData.reason !== "other"
                  ? reasonMap[resignationData.reason] || "--"
                  : resignationData?.otherReason || "--"
                : "--"
            }
          />

          <ResignationField
            label="Notice Period Days"
            value={resignationData?.noticePeriodDays}
          />
          {isAdminOrHr && resignationData.status === 2 ? (
            <div className="col-md-4">
              <span className="fw-bold">Actual Last Working Date*</span>
              <DatePicker
                value={actualLastWorkingDate}
                onChange={(newDate) => setActualLastWorkingDate(newDate)}
                minDate={effectiveDate}
                format="dd MMM yyyy"
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    size: "small",
                    label: "",
                  },
                }}
              />
            </div>
          ) : (
            <ResignationField
              label={
                <span>
                  Actual Last Working Day
                  {isAdminOrHr && resignationData.status === 5 && (
                    <Tooltip
                      title="Edit Actual Last Working Day"
                      arrow
                      placement="top"
                    >
                      <EditIcon
                        className="cursor-pointer"
                        fontSize="small"
                        onClick={() => {
                          handleModalOpen(
                            "Edit Actual Last Working Day",
                            "editLWD"
                          );
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
              }
              value={defaultDateFormatter(resignationData.actualLastWorkingDay)}
            />
          )}
        </div>

        <div className={`row ${showBuyoutDaysField ? "mt-1" : ""}`}>
          <ResignationField
            label="Desired Last Working Day"
            value={defaultDateFormatter(resignationData.desiredLastWorkingDay)}
          />
          <ResignationField
            label="Buy Out Requested"
            value={
              resignationData?.buyout
                ? `Yes (${resignationData?.buyoutDays} days)`
                : "No"
            }
          />
          {resignationData?.buyout &&
            resignationData.status >= 5 &&
            resignationData.status !== 6 && (
              <>
                <ResignationField
                  label="Approved Buyout Days"
                  value={resignationData?.approvedBuyoutDays}
                />
                <ResignationField
                  label="Payable Amount"
                  value={resignationData?.payableAmount}
                />
              </>
            )}

          {showBuyoutDaysField && (
            <>
              <div className="col-md-4">
                <span className="fw-bold">Approved Buyout Days*</span>
                <TextField
                  type="number"
                  value={buyoutDays}
                  variant="standard"
                  fullWidth
                  size="small"
                  onChange={(e) => setBuyoutDays(e.target.value)}
                  sx={{ backgroundColor: "white" }}
                />
              </div>
              <div className="col-md-4">
                <span className="fw-bold">Payable Amount*</span>
                <TextField
                  type="number"
                  value={payableAmount}
                  variant="standard"
                  fullWidth
                  size="small"
                  placeholder="Enter amount"
                  onChange={(e) => setPayableAmount(e.target.value)}
                  sx={{ backgroundColor: "white" }}
                />
              </div>
            </>
          )}

          <ResignationField label="Status" value={status} />
          <ResignationField
            label="Applied Date"
            value={defaultDateFormatter(resignationData?.createdAt)}
          />

          {isAdminOrHr && resignationData.status === 2 && (
            <div className="col-md-4">
              <span className="fw-bold">Effective Date*</span>
              <DatePicker
                value={effectiveDate}
                onChange={(newDate) => setEffectiveDate(newDate)}
                minDate={
                  resignationData?.createdAt
                    ? new Date(resignationData.createdAt)
                    : undefined
                }
                maxDate={actualLastWorkingDate}
                format="dd MMM yyyy"
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    size: "small",
                    label: "",
                  },
                }}
              />
            </div>
          )}
          {resignationData.status >= 5 && resignationData.status !== 6 && (
            <ResignationField
              label="Effective Date"
              value={
                resignationData?.effectiveDate
                  ? defaultDateFormatter(resignationData.effectiveDate)
                  : "--"
              }
            />
          )}
        </div>
      </BoxContainer>
      {/* employee documents table */}
      <BoxContainer>
        <DocumentsTable
          documentsData={resignationData.documents}
          heading="Employee Documents"
          showAddButton={
            resignationData?.hrDocuments?.length > 0 && !isAdminOrHr
          }
          onModalOpen={handleModalOpen}
          action="empUpload"
          resignationId={resignationId}
          docs="empDocuments"
          fetchResignations={() => fetchResignationDetails(resignationId)}
        />
      </BoxContainer>
      {/* hr documents table */}
      {/* showing HR documents only to admin or HR roles, or if there are HR documents provided */}
      {((isEmployee && resignationData.status > 0) || isAdminOrHr) && (
        <BoxContainer>
          <DocumentsTable
            documentsData={resignationData.hrDocuments}
            heading="HR Documents"
            showAddButton={
              isAdminOrHr &&
              resignationData.status !== 1 &&
              resignationData.status !== 6 &&
              resignationData.status !== 3 &&
              resignationData.status !== 9
            }
            onModalOpen={handleModalOpen}
            action="hrUpload"
            resignationId={resignationId}
            docs="hrDocuments"
            fetchResignations={() => fetchResignationDetails(resignationId)}
          />
        </BoxContainer>
      )}
      {/* remarks table */}
      <BoxContainer>
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <h2>Remark History</h2>
            </div>
          </div>
          <div className="row">
            <TableLayout>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Actioner Name</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Comment</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resignationData?.remarks?.length > 0 ? (
                    resignationData.remarks.map((remark, index) => (
                      <TableRow key={index}>
                        <TableCell>{remark?.actionerName ?? "--"}</TableCell>
                        <TableCell>{statusMap[remark.status]}</TableCell>
                        <TableCell>
                          {remark?.comment?.slice(0, 50) +
                            (remark?.comment?.length > 50 ? "..." : "") ?? "--"}
                          {remark?.comment?.length > 50 && (
                            <Tooltip title="Read more" placement="top" arrow>
                              <InfoOutlinedIcon
                                onClick={() => {
                                  showModal(
                                    "Comment",
                                    <p style={{ wordWrap: "break-word" }}>
                                      {remark?.comment}
                                    </p>
                                  );
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{remark.createdAt}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan="4">No remarks available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableLayout>
          </div>
        </div>
      </BoxContainer>
      {isEmployee &&
        canWithdraw(resignationData.createdAt, resignationData.status) && (
          <div className="row">
            <div className="col-12 text-end mt-2">
              <Button
                variant="contained"
                size="medium"
                color="success"
                sx={{ textTransform: "none" }}
                onClick={() => {
                  handleModalOpen("Withdraw Resignation", "employee-withdraw");
                }}
              >
                Withdraw Resignation
              </Button>
            </div>
          </div>
        )}
      <div className="row">
        <div className="d-flex justify-content-end gap-2 mt-2">
          {resignationData.status === 2 && isAdminOrHr && (
            <>
              <CustomizedButton
                variant="success"
                onClick={() => {
                  handleModalOpen("Accept Resigation", "hraccept");
                }}
              >
                Accept
              </CustomizedButton>
              <CustomizedButton
                variant="dark"
                onClick={() => {
                  handleModalOpen("Reject Resigation", "hrreject");
                }}
              >
                Reject
              </CustomizedButton>
            </>
          )}
          {showRetainButton && (
            <CustomizedButton
              variant="dark"
              onClick={() => {
                handleModalOpen("Retain Employee Resigation", "hr-retain");
              }}
            >
              Retain
            </CustomizedButton>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewResignation;
